import { legalCompanyDetails } from "@mealmanager/legal-content";

export type FaqQuestion = {
  id: string;
  question: string;
  answer: readonly string[];
};

export type FaqSection = {
  id: string;
  title: string;
  description: string;
  questions: readonly FaqQuestion[];
};

export type PublicFaqItem = {
  id: string;
  question: string;
  answer: readonly string[];
};

export const publicFaqItems: readonly PublicFaqItem[] = [
  {
    id: "is-realvore-free",
    question: "Is Realvore free?",
    answer: [
      "Yes! Realvore is currently free while we're in beta. We're using this time to gather feedback, improve the app and build the best possible experience. We'd love you to join us and help shape what's next.",
    ],
  },
  {
    id: "what-makes-realvore-different",
    question: "What makes Realvore different?",
    answer: [
      "Realvore brings together everything you need to plan, cook and eat more real food—all in one place.",
      "Alongside hundreds of recipes, you'll find meal planning, automatic shopping lists, detailed nutrition insights and Sous Chef, our AI recipe assistant. We also go beyond traditional calorie and macro tracking with unique features like the Whole Food Score, Plant Diversity Score and Fruit & Veg Portions, helping you understand not just how much you're eating, but the quality and variety of your diet too.",
      "Whether you're looking to eat more whole foods, follow a low-carb lifestyle or simply make healthier choices, Realvore gives you the tools to make it simpler.",
    ],
  },
  {
    id: "keto-low-carb-only",
    question: "Is Realvore only for keto or low-carb diets?",
    answer: [
      "Not at all. Realvore is built around real, minimally processed food, not any single diet. While many of our recipes are naturally lower in carbohydrates, you'll also find recipes to suit a range of eating styles, including high protein, vegetarian, vegan and more.",
    ],
  },
  {
    id: "what-is-sous-chef",
    question: "What is Sous Chef?",
    answer: [
      "Sous Chef is Realvore's AI recipe assistant, designed to make cooking and meal planning a little easier. It can help you create new recipes, tweak existing ones and automatically calculate nutrition information.",
      "Unlike a general AI chatbot, Sous Chef saves everything directly to your Realvore library, so your recipes, cooking instructions and nutrition information are always in one place.",
      "Sous Chef is currently in beta, and we're improving it all the time. We'd love your feedback as we continue to make it smarter and more helpful.",
    ],
  },
  {
    id: "create-own-recipes",
    question: "Can I create my own recipes?",
    answer: [
      "Absolutely. You can create your own recipes from scratch or use Sous Chef to generate, adapt and save recipes directly to your personal library. They're yours to edit, organise and add to your meal plans whenever you like.",
    ],
  },
  {
    id: "nutrition-accuracy",
    question: "How accurate is the nutrition information?",
    answer: [
      "We work hard to make our nutrition information as accurate as possible. Wherever we can, we use trusted food composition databases, with any gaps filled using supermarket data, carefully reviewed AI estimates or manual research.",
      "No nutrition database is perfect, but we're committed to using the best information available and continually improving the quality of our data.",
    ],
  },
  {
    id: "supported-devices",
    question: "What devices can I use Realvore on?",
    answer: [
      "Realvore is currently available on the web while we're in beta, so you can access it from any modern web browser on your computer, tablet or mobile device. We're also working on dedicated iOS and Android apps, which will be launching soon.",
    ],
  },
  {
    id: "count-calories",
    question: "Do I need to count calories?",
    answer: [
      "Not at all. Realvore works whether you like to keep things simple or dive into the detail. Realvore gives you the tools to understand your nutrition—from calories and macros to plant diversity and whole food scores—but you decide how much detail you want to see. Whether you simply want to eat more real food or you're working towards a specific nutrition goal, it's entirely up to you.",
    ],
  },
];

export const faqSections: readonly FaqSection[] = [
  {
    id: "about",
    title: "About Realvore",
    description: "The principles behind our recipes, food scores and Sous Chef.",
    questions: [
      {
        id: "whole-food-recipes",
        question: "Are all Realvore recipes whole-food recipes?",
        answer: [
          "Most of them, yes. The vast majority of Realvore recipes are made with whole and minimally processed ingredients. But we're realistic: eating 100% whole foods isn't always practical, and processed foods can still be part of a balanced diet. Rather than pretending they don't exist, we show you exactly what's in every recipe.",
          "Every recipe includes a Whole Food Score, and each ingredient is labelled as whole, minimally processed, processed or ultra-processed. No judgement—just complete transparency so you can make informed choices.",
        ],
      },
      {
        id: "recipe-sources",
        question: "Where do Realvore recipes come from?",
        answer: [
          "Recipes come from a mix of trusted sources. Some are timeless classics, others are our own creations, and some are adapted from blogs, cookbooks or social media.",
          "Whenever we adapt someone else's recipe, we always credit the original creator. Many recipes have been carefully adapted to use more whole foods and improve their nutritional profile, and wherever possible we cook and test them before they're added to Realvore.",
        ],
      },
      {
        id: "sous-chef",
        question: "What is Sous Chef?",
        answer: [
          "Sous Chef is Realvore's AI recipe assistant, here to make cooking and meal planning a little easier. Whether you want to create a brand-new recipe, tweak one you already love or adjust the macros, Sous Chef can do it in seconds.",
          "Unlike a general AI chatbot, Sous Chef automatically saves your recipes, cooking instructions and nutrition information straight into your Realvore library, so there's no copying and pasting or losing your work.",
          "Sous Chef can be powered by leading AI models from OpenAI, Google and Anthropic. The provider used may vary by feature as we continue to improve the service. Sous Chef is currently in beta, and your feedback genuinely helps shape what's next—we'd love to hear what you think.",
          "We're also working on even more features, including meal plan optimisation and smarter recipe importing from websites and social media. Watch this space!",
        ],
      },
      {
        id: "sous-chef-credits",
        question: "What are Sous Chef credits?",
        answer: [
          "Credits are simply how Sous Chef keeps track of your AI usage—more complex requests use more credits than simpler ones, much like today's AI chatbots.",
          "Each month you'll receive a fresh bundle of credits to use across both the web and mobile app. While there's no fixed number of recipes you can create, most people can expect to generate around 100 recipes each month.",
          "You can check your remaining credits at any time by selecting View Limits—the percentage shows what you've got left. For now, credits are completely free while we continue improving Sous Chef, so we'd love you to experiment and let us know what you think.",
        ],
      },
      {
        id: "skipped-meals",
        question: "What if I skip a meal or eat something else?",
        answer: [
          "No problem! Simply remove the meal from your plan and add what you actually ate using Sous Chef. Your nutrition summary will automatically update to reflect your changes.",
        ],
      },
      {
        id: "keto-low-carb-only",
        question: "Is Realvore only for low-carb or keto diets?",
        answer: [
          "Not at all. Realvore is built around real, minimally processed food, not any single diet. While many of our recipes are naturally lower in carbohydrates, you'll also find recipes to suit a range of eating styles, including high protein, vegetarian, vegan and more.",
        ],
      },
    ],
  },
  {
    id: "nutrition",
    title: "Crunching the numbers",
    description: "How we source nutrition data and calculate the scores in your recipes and meal plans.",
    questions: [
      {
        id: "macro-data",
        question: "What are macros, and where does your nutrition data come from?",
        answer: [
          "Macros (short for macronutrients) are the nutrients that provide your body with energy: carbohydrates, protein and fat. Looking at your macros helps you understand more than just calories, making it easier to eat in a way that supports your goals—whether that's losing weight, building muscle, following a low-carb lifestyle or simply eating a more balanced diet.",
          "We take nutrition data seriously. Wherever possible, our nutrition information comes from trusted food composition databases, with any gaps filled using supermarket data, carefully reviewed AI estimates or manual research. Here's where our data comes from, in order of priority:",
          "35% from McCance and Widdowson's Composition of Foods Integrated Dataset (CoFID) 2021 – the UK's gold-standard food composition database.",
          "31% from the US Department of Agriculture's FoodData Central database.",
          "21% from UK supermarket websites, where data isn't available from the sources above.",
          "10% estimated using AI platforms, including ChatGPT and Claude.",
          "3% sourced and entered manually, for example specialist keto ingredients.",
          "No nutrition database is perfect, but we're committed to using the best information available and continually improving the quality of our data.",
        ],
      },
      {
        id: "net-carbs",
        question: "What are net carbs, and how do you calculate them?",
        answer: [
          "Net carbs are the carbohydrates your body digests and uses for energy. They're calculated by subtracting dietary fibre from the total carbohydrates in a food, since fibre isn't digested in the same way and has little impact on blood sugar.",
          "At Realvore, we calculate net carbs by subtracting fibre from total carbohydrates. Because we focus on whole, home-cooked food—where sugar alcohols (polyols) are rarely used—we don't subtract them across the app. However, we've made specific adjustments for common keto products, such as low-carb bread, pasta, wraps and baking ingredients, where sugar alcohols can make up a significant proportion of the recipe.",
          "Tracking net carbs can be especially helpful if you're following a ketogenic diet or managing diabetes, as it focuses on the carbohydrates most likely to affect blood sugar—without discouraging naturally fibre-rich foods.",
        ],
      },
      {
        id: "plant-diversity",
        question: "What is the plant diversity score, and how is it calculated?",
        answer: [
          "Your plant diversity score (sometimes called plant points) measures the variety of plants you eat over the course of a week—not how much you eat. Research suggests that eating a wider variety of plants supports a healthier gut microbiome, which is linked to better digestive, metabolic and immune health. A common goal is to aim for 30 or more plant points each week.",
          "Here's how we calculate your score:",
          "Each different fruit, vegetable, legume, wholegrain, nut or seed = 1 point.",
          "Herbs, spices, garlic, olive oil, tea, coffee and cocoa = ¼ point each.",
          "Eating the same plant more than once during the week doesn't increase your score—variety is the whole point! And whether your plants are fresh, frozen, tinned or dried, they all count equally.",
        ],
      },
      {
        id: "whole-food-score",
        question: "What is the Whole Food Score, and how is it calculated?",
        answer: [
          "The Whole Food Score gives you a quick way to see how much of a recipe is made from whole and minimally processed ingredients. Scores range from 0 to 100—the higher the score, the more of the recipe comes from whole foods.",
          "To calculate the score, we classify every ingredient using the NOVA food classification system, a widely recognised framework for categorising foods by their level of processing.",
          "Whole foods (100 points) – Foods in their natural state, such as fresh fruit and vegetables, eggs, meat and fish.",
          "Minimally processed (75 points) – Whole foods that have been prepared to make them safe or convenient, like frozen vegetables, plain yoghurt, rolled oats and pasteurised milk.",
          "Processed (40 points) – Foods made by combining whole foods with a small number of everyday ingredients, such as salt, oil or vinegar. Examples include cheese, canned tuna and traditional bread.",
          "Ultra-processed (0 points) – Foods made using industrial processing and ingredients that aren't typically found in a home kitchen, such as soft drinks, instant noodles and many packaged snacks.",
          "We then calculate a weighted average based on the amount of each ingredient in a serving (excluding added water, stock, herbs and spices) and round the result to the nearest whole number.",
          "The Whole Food Score isn't about labelling foods as \"good\" or \"bad.\" It's simply a transparent way to help you understand how processed a recipe is, so you can make informed choices that work for you.",
        ],
      },
      {
        id: "veg-portions",
        question: "What are veg portions, and how do you calculate them?",
        answer: [
          "Veg portions show how many portions of fruit and vegetables are in each recipe, based on the UK NHS 5 A Day guidance.",
          "We calculate portions using the following serving sizes:",
          "80g of fresh, frozen or tinned fruit and vegetables = 1 portion.",
          "30g of dried fruit = 1 portion.",
          "Health experts recommend eating at least 5 portions of fruit and vegetables a day as part of a healthy, balanced diet.",
          "A couple of things to note: white potatoes, yams, cassava and plantains are counted as starchy carbohydrates, so they don't contribute towards your fruit and veg portions. Sweet potatoes, parsnips and turnips do count.",
        ],
      },
      {
        id: "weekly-nutrition",
        question: "How is the nutrition in my meal plan calculated?",
        answer: [
          "Your meal plan dashboard gives you an easy-to-understand overview of your nutrition for the week. Here's how we calculate each metric:",
          "Macros – We calculate the average daily calories and macros by adding together the nutrition for each meal in your plan.",
          "Plant Diversity Score – We count each unique plant just once across all the recipes in your meal plan to give you your total score for the week.",
          "Fruit & Veg Portions – We calculate the average number of fruit and veg portions you eat each day across your meal plan.",
          "Whole Food Score – We take the average Whole Food Score across all the recipes in your meal plan.",
          "These summaries are designed to give you a simple snapshot of your overall nutrition, making it easy to see how balanced your week looks at a glance.",
        ],
      },
      {
        id: "nutrition-filters",
        question: "How are the nutrition filters defined?",
        answer: [
          "Our nutrition filters use consistent thresholds to help you quickly find recipes that match the way you like to eat.",
          "Strict keto – 3g or fewer net carbs per serving.",
          "Keto – 8g or fewer net carbs per serving.",
          "Low carb – 25g or fewer net carbs per serving.",
          "High protein – 25g or more protein per serving.",
          "High fibre – 8g or more fibre per serving.",
          "6+ Plants – 6 or more unique plants per serving.",
          "Whole food – Recipes with a Whole Food Score of 80 or above.",
          "Looking for something more specific? You can also use Advanced Filters to filter recipes by the exact amount of calories, protein, fat, carbohydrates, fibre, net carbs, plant diversity and Whole Food Score.",
        ],
      },
    ],
  },
  {
    id: "help",
    title: "Help me out",
    description: "Practical help for creating recipes, planning meals and getting in touch.",
    questions: [
      {
        id: "edit-recipe",
        question: "Can I edit a recipe?",
        answer: [
          "Yes! Open the recipe, tap the menu and select Edit Recipe.",
          "You can edit any recipe you've created yourself. If you'd like to tweak a Realvore recipe, simply ask Sous Chef to create your own version with the changes you'd like.",
        ],
      },
      {
        id: "manual-recipe",
        question: "Can I create a recipe manually?",
        answer: [
          "Absolutely. Head to Add in the Recipes tab, enter your ingredients and cooking steps, and we'll calculate the nutrition for you automatically—no calculator required.",
        ],
      },
      {
        id: "delete-recipe",
        question: "Can I delete a recipe?",
        answer: [
          "Yes! You can delete any recipe you've created by opening the menu from either the recipe card or within the recipe, then selecting Delete Recipe.",
          "To protect the original collection, Realvore recipes can't be deleted. If you'd like to make changes to one, simply ask Sous Chef to create your own version instead.",
        ],
      },
      {
        id: "recipe-images",
        question: "Can I upload photos of my recipes?",
        answer: [
          "Yes! Open the recipe, tap the menu, select Edit Recipe, and add your own photo to make it your own.",
        ],
      },
      {
        id: "recipe-import",
        question: "Can I import recipes from social media or the internet?",
        answer: [
          "Not just yet, but we're working on it! In the meantime, simply copy and paste the recipe into Sous Chef, and it'll create the recipe for you, including the cooking instructions and nutrition information.",
        ],
      },
      {
        id: "household-shopping-items",
        question: "How do I add household items to my shopping list?",
        answer: [
          "Easy—from the shopping list tab, tap Add to include any other household or food items alongside your meal plan ingredients. One list, whole shop, done.",
        ],
      },
      {
        id: "app-issues",
        question: "I'm having issues with the app, or I'd like to share feedback",
        answer: [
          "We're always looking for ways to make Realvore better. If you've found a bug, have a suggestion or just want to share some feedback, we'd love to hear from you.",
          `You can get in touch via Contact Us in your account or email us at ${legalCompanyDetails.contactEmail}.`,
        ],
      },
      {
        id: "nutrition-accuracy",
        question: "I think the nutrition information is wrong—what should I do?",
        answer: [
          "We do our best to provide accurate nutrition information, but no food database is perfect. If you spot something that doesn't look right, let us know using Contact Us and we'll investigate.",
        ],
      },
      {
        id: "restaurant-recipes",
        question: "Can I import restaurant recipes to help me keep track of my macros?",
        answer: [
          "Not yet, no. We're working on it.",
        ],
      },
    ],
  },
];
